// components/UserAccountDetails.vue
<template>
  <div class="p-6">
    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-gray-900">User Account Details</h1>
      <button
        @click="goBack"
        class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition duration-200"
      >
        Back to List
      </button>
    </div>

    <!-- Error Message -->
    <div
      v-if="error"
      class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
      role="alert"
    >
      <strong class="font-bold">Error!</strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>

    <!-- Loading Message -->
    <div v-if="isLoading" class="text-gray-600 mb-4">
      Loading user account details...
    </div>

    <!-- User Account Details -->
    <div
      v-if="!isLoading && !error && user"
      class="bg-white shadow-md rounded-lg p-6"
    >
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="col-span-2">
          <h2 class="text-xl font-semibold mb-2">Basic Information</h2>
        </div>

        <div>
          <p class="text-sm text-gray-600">Username</p>
          <p class="font-medium">{{ user.username }}</p>
        </div>

        <div>
          <p class="text-sm text-gray-600">Organisation</p>
          <p class="font-medium">{{ user.org || "N/A" }}</p>
        </div>

        <div>
          <p class="text-sm text-gray-600">First Name</p>
          <p class="font-medium">{{ user.first_name || "N/A" }}</p>
        </div>

        <div>
          <p class="text-sm text-gray-600">Last Name</p>
          <p class="font-medium">{{ user.last_name || "N/A" }}</p>
        </div>

        <div>
          <p class="text-sm text-gray-600">Date Joined</p>
          <p class="font-medium">{{ formatDateTime(user.date_joined) }}</p>
        </div>

        <div>
          <p class="text-sm text-gray-600">Last Login</p>
          <p class="font-medium">{{ formatDateTime(user.last_login) }}</p>
        </div>

        <div class="col-span-2">
          <h2 class="text-xl font-semibold mb-2 mt-4">Account Status</h2>
        </div>

        <div>
          <p class="text-sm text-gray-600">Account Status</p>
          <p
            :class="[
              'font-medium',
              user.is_active ? 'text-green-600' : 'text-red-600',
            ]"
          >
            {{ user.is_active ? "Active" : "Inactive" }}
          </p>
        </div>

        <div>
          <p class="text-sm text-gray-600">Account Type</p>
          <p class="font-medium">
            {{
              user.is_superuser
                ? "Superuser"
                : user.is_staff
                ? "Staff"
                : "Regular User"
            }}
          </p>
        </div>

        <div class="col-span-2">
          <h2 class="text-xl font-semibold mb-2 mt-4">Permissions</h2>
          <div class="bg-gray-100 p-4 rounded-md">
            <div v-if="user.permissions && user.permissions.length">
              <ul class="list-disc list-inside">
                <li
                  v-for="permission in user.permissions"
                  :key="permission"
                  class="text-gray-700"
                >
                  {{ permission }}
                </li>
              </ul>
            </div>
            <p v-else class="text-gray-700">
              No specific permissions assigned.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { getUserDetails } from "@/services/userAccountService";

export default {
  name: "UserAccountDetails",
  setup() {
    const route = useRoute();
    const router = useRouter();
    const user = ref(null);
    const error = ref(null);
    const isLoading = ref(false);

    const fetchUserDetails = async () => {
      isLoading.value = true;
      try {
        const userId = route.params.id;
        user.value = await getUserDetails(userId);
        error.value = null;
      } catch (err) {
        console.error("Error fetching user details:", err);
        error.value = "Failed to fetch user details. Please try again later.";
        user.value = null;
      } finally {
        isLoading.value = false;
      }
    };

    const formatDateTime = (dateString) => {
      if (!dateString) return "Never";
      const options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    };

    const goBack = () => {
      router.push({ name: "UserAccounts" });
    };

    onMounted(fetchUserDetails);

    return {
      user,
      error,
      isLoading,
      formatDateTime,
      goBack,
    };
  },
};
</script>
